// eslint-disable-next-line import/no-cycle
import {
  getTemplateIframeDocument,
  getElementFromIframe,
  createNewStylesheet,
} from '@/components/template-builder/utils/helpers';

export interface WidgetCssRule {
  selector: string;
  properties: string;
}

export interface WidgetCssConfig {
  static?: WidgetCssRule[];
  static_persistent_mobile?: WidgetCssRule[];
}

const widgetCssRegistry: Map<string, WidgetCssConfig> = new Map();

export const registerWidgetCss = (dataWidgetType: string, config: WidgetCssConfig) => {
  widgetCssRegistry.set(dataWidgetType, config);
};

export const getWidgetCssConfig = (dataWidgetType: string): WidgetCssConfig | undefined => widgetCssRegistry.get(dataWidgetType);

export const getAllRegisteredWidgetTypes = (): string[] => Array.from(widgetCssRegistry.keys());

const getCssMarker = (dataWidgetType: string): string => `/* widget-css:${dataWidgetType} */`;

const escapeForRegex = (str: string): string => str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

/**
 * Check if a widget with the given data-widgettype attribute exists in the template
 */
const isWidgetTypePresent = (dataWidgetType: string): boolean => {
  const template = getTemplateIframeDocument();
  if (!template) return false;
  return template.querySelector(`[data-widgettype="${dataWidgetType}"]`) !== null;
};

/**
 * Build a CSS block string from an array of WidgetCssRule
 */
const buildCssBlock = (rules: WidgetCssRule[]): string => rules
  .map((rule) => `${rule.selector} { ${rule.properties} }`)
  .join('\n');

/**
 * Inject widget CSS into static and/or static_persistent_mobile stylesheets.
 * Uses markers to prevent duplicate injection.
 * Should be called after a widget is added to the template.
 */
export const injectWidgetCss = (dataWidgetType: string): void => {
  const config = getWidgetCssConfig(dataWidgetType);
  if (!config) return;

  const template = getTemplateIframeDocument();
  if (!template) return;

  const marker = getCssMarker(dataWidgetType);

  // --- static stylesheet ---
  if (config.static && config.static.length > 0) {
    let staticSheet = getElementFromIframe('style[data-spm-styles="static"]');
    if (!staticSheet) {
      staticSheet = createNewStylesheet('static');
    }
    if (staticSheet && !staticSheet.innerHTML.includes(marker)) {
      const cssBlock = buildCssBlock(config.static);
      staticSheet.innerHTML += `\n${marker}\n${cssBlock}\n${marker}\n`;
    }
  }

  // --- static_persistent_mobile stylesheet ---
  if (config.static_persistent_mobile && config.static_persistent_mobile.length > 0) {
    let mobileSheet = getElementFromIframe('style[data-spm-styles="static_persistent_mobile"]');
    if (!mobileSheet) {
      mobileSheet = createNewStylesheet('static_persistent_mobile');
    }
    if (mobileSheet && !mobileSheet.innerHTML.includes(marker)) {
      const cssBlock = buildCssBlock(config.static_persistent_mobile);
      const mediaRegex = /@media\s+only\s+screen\s+and\s+\(max-width:\s*600px\)\s*\{/;

      const mediaMatch = mobileSheet.innerHTML.match(mediaRegex);
      if (mediaMatch && mediaMatch.index !== undefined) {
        // Find the matching closing brace of the 600px @media block
        const openBracePos = mediaMatch.index + mediaMatch[0].length - 1;
        let depth = 1;
        let closeBracePos = -1;
        for (let i = openBracePos + 1; i < mobileSheet.innerHTML.length; i++) {
          if (mobileSheet.innerHTML[i] === '{') depth++;
          if (mobileSheet.innerHTML[i] === '}') depth--;
          if (depth === 0) { closeBracePos = i; break; }
        }
        if (closeBracePos !== -1) {
          mobileSheet.innerHTML = `${mobileSheet.innerHTML.substring(0, closeBracePos)}\n${marker}\n${cssBlock}\n${marker}\n${mobileSheet.innerHTML.substring(closeBracePos)}`;
        }
      } else {
        // Create the @media wrapper
        mobileSheet.innerHTML += `\n@media only screen and (max-width: 600px) {\n${marker}\n${cssBlock}\n${marker}\n}\n`;
      }
    }
  }
};

/**
 * Remove widget CSS from static and static_persistent_mobile stylesheets
 * if no widget of the given type remains in the template.
 * Should be called after a widget is removed from the template.
 */
export const removeWidgetCssIfUnused = (dataWidgetType: string): void => {
  const config = getWidgetCssConfig(dataWidgetType);
  if (!config) return;

  // If at least one widget of this type still exists, keep the CSS
  if (isWidgetTypePresent(dataWidgetType)) return;

  const marker = getCssMarker(dataWidgetType);
  const escapedMarker = escapeForRegex(marker);
  const markerRegex = new RegExp(`\\n?${escapedMarker}[\\s\\S]*?${escapedMarker}\\n?`, 'g');

  // Clean static stylesheet
  const staticSheet = getElementFromIframe('style[data-spm-styles="static"]');
  if (staticSheet && staticSheet.innerHTML.includes(marker)) {
    staticSheet.innerHTML = staticSheet.innerHTML.replace(markerRegex, '');
  }

  // Clean static_persistent_mobile stylesheet
  const mobileSheet = getElementFromIframe('style[data-spm-styles="static_persistent_mobile"]');
  if (mobileSheet && mobileSheet.innerHTML.includes(marker)) {
    mobileSheet.innerHTML = mobileSheet.innerHTML.replace(markerRegex, '');
  }
};

/**
 * Cleanup CSS for all registered widget types that are no longer present in the template.
 * Can be called as a general cleanup pass.
 */
export const cleanupAllUnusedWidgetCss = (): void => {
  getAllRegisteredWidgetTypes().forEach((widgetType) => {
    removeWidgetCssIfUnused(widgetType);
  });
};
